import { track } from "@/lib/track";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check, Phone } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import SubmissionForm from "@/components/SubmissionForm";
import { useUI } from "@/context/UIContext";
import { btnPrimary, btnOutline, overline } from "@/components/kit";
import { SERVICES, CONTACT } from "@/data/site";

// Map of SEO keywords per service slug — Google-friendly long-tail queries.
const SERVICE_KEYWORDS = {
  "lancements-produits": "lancement produit entreprise, agence lancement produit, révélation produit, presse et lancement, event lancement produit Lyon Paris, product launch B2B",
  "inaugurations-boutiques": "inauguration boutique, agence ouverture boutique, événement inauguration entreprise, pose première pierre, inauguration magasin, retail event",
  "soirees-galas": "soirée de gala entreprise, dîner de gala, agence gala Rhône-Alpes, gala B2B, soirée entreprise Paris, cérémonie de prestige",
  "seminaires-conventions": "organisation séminaire Lyon, séminaire sur-mesure Paris, agence séminaire entreprise, convention d'entreprise, séminaire d'intégration, convention B2B",
  "team-building": "team building Lyon, team building Paris, agence team building, cohésion équipe, animation team building, séjour cohésion",
  "conferences-congres": "organisation congrès, agence conférence, congrès scientifique, événement corporate 5000 personnes, régie congrès Paris Lyon",
  "fetes-noel-entreprise": "fête de Noël entreprise, arbre de Noël CE, soirée fin d'année entreprise, dîner de Noël pro, animation Noël enfants entreprise, fête de fin d'année Lyon Paris",
  "voyages-incentives": "voyage incentive, team building étranger, incentive Maroc, team building Espagne, incentive Antilles, séminaire à l'étranger, voyage d'entreprise Croatie Suisse Belgique Canada",
};

const LYON_LINKS = {
  "lancements-produits": { label: "Organisation de lancement de produit à Lyon", to: "/lancement-produit-lyon" },
  "inaugurations-boutiques": { label: "Organisation d'inauguration à Lyon", to: "/inauguration-entreprise-lyon" },
  "soirees-galas": { label: "Organisation de soirée d'entreprise à Lyon", to: "/soiree-entreprise-lyon" },
  "seminaires-conventions": { label: "Organisation de séminaire à Lyon", to: "/seminaire-convention-lyon" },
  "conferences-congres": { label: "Salon professionnel et congrès à Lyon", to: "/salon-professionnel-lyon" },
  "fetes-noel-entreprise": { label: "Fête de Noël d'entreprise à Lyon", to: "/fete-de-noel-entreprise/lyon" },
};

export default function PrestationDetail() {
  const { slug } = useParams();
  const { setCallbackOpen } = useUI();
  const service = SERVICES.find((s) => s.slug === slug);
  if (!service) return <Navigate to="/prestations" replace />;

  const idx = SERVICES.findIndex((s) => s.slug === slug);
  const next = SERVICES[(idx + 1) % SERVICES.length];
  const keywords = SERVICE_KEYWORDS[slug];
  const lyonLink = LYON_LINKS[slug];

  return (
    <>
      <Seo title={service.title} description={service.short} keywords={keywords} path={`/prestations/${slug}`} />

      <section className="relative h-[70vh] min-h-[520px] overflow-hidden pt-20" data-testid="prestation-hero">
        <img src={service.image} alt={service.title} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-black/45" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
        <div className="relative z-10 mx-auto flex h-full max-w-[1440px] flex-col justify-end px-5 pb-16 md:px-10 md:pb-24">
          <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-[11px] uppercase tracking-[0.28em] font-semibold text-lime">Prestation</motion.span>
          <motion.h1 initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }} className="mt-4 max-w-4xl font-serif text-5xl font-medium leading-none tracking-tight text-white md:text-8xl">
            {service.title}
          </motion.h1>
          <p className="mt-5 font-serif text-2xl italic text-lime">{service.tagline}</p>
        </div>
      </section>

      {/* Maillage : expertise nationale → page locale Lyon */}
      {lyonLink && (
        <section className="border-b border-ink/10 bg-mist" data-testid="prestation-lyon-link">
          <div className="mx-auto flex max-w-[1440px] flex-wrap items-center justify-between gap-4 px-5 py-6 md:px-10">
            <p className="text-sm text-ink/60">Votre événement se déroule à Lyon ou dans la métropole ?</p>
            <Link to={lyonLink.to} className="group flex items-center gap-2 rounded-full border border-ink/15 bg-white px-5 py-2.5 text-sm font-medium text-ink transition-colors hover:border-lime-dim" data-testid="prestation-lyon-cta">
              {lyonLink.label} <ArrowRight size={14} className="text-lime-dim transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </section>
      )}

      <section className="mx-auto max-w-[1440px] px-5 py-20 md:px-10 md:py-32">
        <div className="grid gap-16 md:grid-cols-12">
          <div className="md:col-span-5">
            <div className="md:sticky md:top-32">
              <Reveal>
                <span className={overline}>Notre approche</span>
                <p className="mt-6 font-serif text-3xl leading-snug text-ink md:text-4xl">{service.intro}</p>
              </Reveal>
              <Reveal delay={1}>
                <div className="mt-10 flex flex-wrap gap-3">
                  <Link to="/devis" className={btnPrimary} data-testid="prestation-devis-btn">Parlons de votre projet <ArrowRight size={16} /></Link>
                  <button onClick={() => { track("click_rappel"); setCallbackOpen(true); }} className={btnOutline}>Être rappelé</button>
                </div>
              </Reveal>
              <Reveal delay={2}>
                <a href={`tel:${CONTACT.phoneMobile.replace(/\s/g, "")}`} className="mt-6 flex items-center gap-3 text-sm text-ink/60 hover:text-lime-dim" data-testid="prestation-phone">
                  <Phone size={16} className="text-lime-dim" />
                  <span>Ou appelez-nous directement au <strong className="text-ink">{CONTACT.phoneMobile}</strong></span>
                </a>
              </Reveal>
              {slug === "voyages-incentives" && (
                <Reveal delay={3}>
                  <Link to="/voyages" className="mt-8 flex items-center justify-between gap-3 rounded-xl border border-lime-dim/40 bg-lime/20 p-5 transition-colors hover:bg-lime/40" data-testid="voyages-hub-link">
                    <div>
                      <div className="text-[11px] uppercase tracking-[0.15em] text-ink/50">8 destinations dédiées</div>
                      <div className="mt-1 font-serif text-lg text-ink">Voir toutes nos destinations →</div>
                    </div>
                    <ArrowRight size={22} className="text-ink" />
                  </Link>
                </Reveal>
              )}
            </div>
          </div>

          <div className="md:col-span-6 md:col-start-7">
            <Reveal><span className={overline}>Ce que nous livrons</span></Reveal>
            <ul className="mt-8 space-y-px overflow-hidden rounded-2xl border border-ink/10">
              {service.deliverables.map((d, i) => (
                <Reveal key={i} delay={i % 3}>
                  <li className="flex items-start gap-4 bg-mist p-6" data-testid={`deliverable-${i}`}>
                    <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-lime text-ink"><Check size={14} /></span>
                    <span className="text-lg text-ink/80">{d}</span>
                  </li>
                </Reveal>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Embedded contact form */}
      <section className="border-t border-ink/10 bg-mist" data-testid="prestation-form-section">
        <div className="mx-auto grid max-w-[1440px] gap-12 px-5 py-20 md:grid-cols-12 md:px-10 md:py-28">
          <div className="md:col-span-5">
            <Reveal><span className={overline}>Discutons de votre {service.title.toLowerCase()}</span></Reveal>
            <Reveal delay={1}>
              <h2 className="mt-6 font-serif text-4xl font-medium leading-tight tracking-tight text-ink md:text-5xl">
                Un projet en tête ?<br /><span className="italic text-lime-dim">Réponse sous 24h.</span>
              </h2>
            </Reveal>
            <Reveal delay={2}>
              <p className="mt-6 max-w-md text-base leading-relaxed text-ink/60">
                Décrivez-nous en quelques lignes votre projet — nous revenons vers vous avec une première proposition dans la journée.
              </p>
            </Reveal>
            <Reveal delay={3}>
              <a href={`tel:${CONTACT.phoneMobile.replace(/\s/g, "")}`} className="mt-8 flex items-center gap-3 rounded-xl border border-ink/15 bg-white p-5 transition-colors hover:border-lime-dim/60" data-testid="prestation-phone-block">
                <Phone size={22} className="text-lime-dim" />
                <div>
                  <div className="text-xs uppercase tracking-[0.15em] text-ink/40">Réponse sous 24 h ouvrées</div>
                  <div className="mt-0.5 font-serif text-2xl text-ink">{CONTACT.phoneMobile}</div>
                </div>
              </a>
            </Reveal>
          </div>
          <div className="md:col-span-7">
            <div className="rounded-2xl border border-ink/10 bg-white p-8 md:p-10">
              <SubmissionForm
                type="devis"
                testid={`prestation-form-${slug}`}
                submitLabel="Envoyer ma demande"
                fields={[
                  { name: "name", label: "Votre nom", required: true },
                  { name: "company", label: "Entreprise", required: true },
                  { name: "email", label: "Email", type: "email", required: true },
                  { name: "phone", label: "Téléphone", type: "tel" },
                  { name: "message", label: `Décrivez votre projet de ${service.title.toLowerCase()}`, type: "textarea", full: true, required: true, placeholder: "Date envisagée, nombre d'invités, budget indicatif, objectif…" },
                ]}
              />
            </div>
          </div>
        </div>
      </section>

      <section className="border-t border-ink/10 bg-white">
        <Link to={`/prestations/${next.slug}`} className="group mx-auto flex max-w-[1440px] items-center justify-between px-5 py-16 md:px-10 md:py-24" data-testid="next-prestation">
          <div>
            <span className={overline}>Prestation suivante</span>
            <h3 className="mt-3 font-serif text-4xl font-medium text-ink group-hover:text-lime-dim md:text-6xl">{next.title}</h3>
          </div>
          <ArrowRight size={40} className="text-ink/40 transition-transform duration-300 group-hover:translate-x-2 group-hover:text-lime-dim" />
        </Link>
      </section>
    </>
  );
}
